import * as React from 'react';
import {
  Plugins,
  TypeImplementation,
  withSystem,
  Type,
  Types,
  getMeta,
} from '@packages/systems/wfdl';
import {
  a,
  enumOf,
  TList,
  functionOf,
  TGeneric,
  TTypeConstructor,
  TTypeApplication,
  EEnum,
  EList,
} from '@packages/systems/wfdl-ast';
import {
  TOperand,
  toOperandExpr,
} from '@packages/systems/plugins/ConditionsOperand';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  ContextualExpression,
  ContextualExpressions,
  getAnn,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {Autocomplete} from '@packages/systems/spring';

export const TEnumAnyOfList = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(Plugins.name('Enum'), typeName('AnyOfList'))),
    arg,
  });

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    AnyOfList: TypeImplementation.Alias({
      params: ['a'],
      type: TList(enumOf(a)),
      render: RenderEnumAnyOfList,
    }),
  },
  values: {
    IsAny: {
      envType: {
        dataType: withSystem(
          {label: 'is any'},
          TGeneric({
            arg: 'a',
            body: functionOf(
              withSystem({isBindable: true}, TEnumAnyOfList(a)),
              TPredicate(enumOf(a))
            ),
          })
        ),
        exported: 'visible',
      },
      value: (predArgList: string[]) => (fieldVal: string) => {
        return toBooleanTag(predArgList.includes(fieldVal));
      },
    },
    IsNotAny: {
      envType: {
        dataType: withSystem(
          {label: 'is not any'},
          TGeneric({
            arg: 'a',
            body: functionOf(
              withSystem({isBindable: true}, TEnumAnyOfList(a)),
              TPredicate(enumOf(a))
            ),
          })
        ),
        exported: 'visible',
      },
      value: (predArgList: string[]) => (fieldVal: string) => {
        return toBooleanTag(!predArgList.includes(fieldVal));
      },
    },
    Condition: {
      envType: {
        dataType: TGeneric({
          arg: 'a',
          body: functionOf(enumOf(a), TOperand(enumOf(a))),
        }),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      expression: toOperandExpr,
    },
  },
});

/**
 * The type that we are rendering here for is `List<Enum>` and we want to
 * render the enum values as options.
 */
function getEnumType(type: Type): Type | undefined {
  const deconstructedType = Types.deconstructTypeApplication(type);
  return deconstructedType ? deconstructedType.args[0] : undefined;
}

function RenderEnumAnyOfList({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const type = getAnn(expr)?.type;
  const enumType = type ? getEnumType(type) : undefined;
  if (!enumType) {
    return null;
  }
  const enumValues = Types.getEnumValues(enumType);
  if (!enumValues) {
    return null;
  }
  const currentValuesExpr = ContextualExpressions.getList(stripAnn(expr));
  const currentValues = currentValuesExpr
    ? currentValuesExpr
        .map((item) => ContextualExpressions.getEnum(stripAnn(item)))
        .filter((value): value is string => value !== null)
    : [];
  const enumListDispatch = (value: string[]) => {
    const list = value.map((v) => EEnum(v));
    dispatch(updateExpressionAction(EList(list)));
  };

  const meta = getMeta(enumType) || {};

  const options = Object.keys(enumValues).map((value) => {
    const labelOverride = meta.optionLabels ? meta.optionLabels[value] : null;
    const label = labelOverride || enumValues[value] || value;
    return {
      value,
      label,
    };
  });

  return (
    <Autocomplete
      aria-label="Select options"
      data-automation-id={`${Types.generateAutomationIds(enumType)}`}
      value={currentValues}
      selectionMode="multiple"
      options={options}
      onChange={(value) => {
        enumListDispatch(value);
      }}
    />
  );
}
